import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { makeStyles } from '@mui/styles'
import { styled, Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem, { listItemClasses } from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import AppIcon from '@mui/icons-material/AppsOutlined'
import useBackoffice from '../../hooks/backoffice/useBackoffice'
import { RootStateType } from '../../redux/reducer/rootReducer'
import { ReactComponent as ProfileIcon } from '../../icon/backoffice_menu_profile.svg'
import { ReactComponent as UserGroupIcon } from '../../icon/backoffice_menu_userGroup.svg'
import { ReactComponent as UserIcon } from '../../icon/backoffice_menu_user.svg'
import { ReactComponent as ContentIcon } from '../../icon/backoffice_menu_content.svg'
import { ReactComponent as ContactIcon } from '../../icon/backoffice_menu_contact.svg'
import { ReactComponent as MuxIcon } from '../../icon/backoffice_menu_mux.svg'
import { ReactComponent as LogoutIcon } from '../../icon/map_menu_logout.svg'

const CoreMenu = () => {
    const { loginProfile } = useSelector(({ profile }: RootStateType) => profile)
    const { menuList, onClickLogout } = useBackoffice()
    const classes = useStyles()

    const [menuData, setMenuData] = useState<BackofficeMenuType[]>([])

    useEffect(() => {
        setMenuData(menuList)
        checkActiveMenu()
    }, [])

    useEffect(() => {
        setMenuData(menuList)
        checkActiveMenu()
    }, [menuList])

    const checkActiveMenu = () => {
        const url = globalThis.location.href
        if (url && !url.includes('not-found')) {
            const filteredMenu = menuList.filter((item: BackofficeMenuType) => url.includes(item.path))

            if (filteredMenu.length > 0) {
                const menu: BackofficeMenuType = filteredMenu[0]
                if (menu) {
                    onClickMenuItem(menu.id)
                }
            }
        }
    }

    const onClickMenuItem = (id: number) => {
        const data = menuList.map((item: BackofficeMenuType) => {
            if (item.id === id) {
                item.active = true
            } else {
                item.active = false
            }
            return item
        })
        setMenuData(data)
    }

    const getMenuIcon = (id: string | number) => {
        switch (id) {
            case 7.1: // จัดการกลุ่มผู้ใช้งาน
                return <UserGroupIcon />
            case 7.2: // จัดการผู้ใช้งาน
                return <UserIcon />
            case 7.3: // จัดการเนื้อหา
                return <ContentIcon />
            case 7.4: // จัดการข้อมูลติดต่อเรา
                return <ContactIcon />
            case 7.5: // จัดการ MUX
                return <MuxIcon />
            default:
                return <AppIcon />
        }
    }

    return (
        <StyledDivMenuContainer>
            <List className={classes.listProfile}>
                <ListItem
                    classes={{
                        root: classes.listItemProfile,
                    }}
                >
                    <ListItemIcon classes={{ root: classes.listItemIconLeft }}>
                        <ProfileIcon />
                    </ListItemIcon>
                    <div>
                        <div style={{ display: 'flex', width: '175px' }}>
                            <StyledTypoName noWrap>{loginProfile?.fullname}</StyledTypoName>
                        </div>
                        <div style={{ display: 'flex', width: '180px' }}>
                            <StyledTypoEmail noWrap>{loginProfile?.email}</StyledTypoEmail>
                        </div>
                    </div>
                </ListItem>
            </List>
            {menuData?.map(
                (item: BackofficeMenuType, index: number) =>
                    item.display && (
                        <List key={`list-${index}`} className={item.active ? classes.listActive : classes.list}>
                            <StyledLink key={`link-${index}`} to={item.path}>
                                <ListItem
                                    button
                                    key={`ListItem-${index}`}
                                    onClick={() => {
                                        onClickMenuItem(item.id)
                                    }}
                                    classes={{
                                        root: item.active ? classes.listItemActive : classes.listItem,
                                        selected: classes.selected,
                                    }}
                                >
                                    <ListItemIcon classes={{ root: classes.listItemIconLeft }}>
                                        {getMenuIcon(item.id)}
                                    </ListItemIcon>
                                    <ListItemText primary={item.name} />
                                </ListItem>
                                <Divider className={classes.divider} />
                                {/* {index < menuData.length - 1 && <Divider className={classes.divider} />} */}
                            </StyledLink>
                        </List>
                    ),
            )}
            <List className={classes.list}>
                <ListItem
                    button
                    onClick={onClickLogout}
                    classes={{
                        root: classes.listItem,
                        selected: classes.selected,
                    }}
                >
                    <ListItemIcon classes={{ root: classes.listItemIconLeft }}>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary={'ออกจากระบบ'} />
                </ListItem>
            </List>
        </StyledDivMenuContainer>
    )
}

export default CoreMenu

const useStyles = makeStyles((theme: Theme) => ({
    list: {
        padding: '0px !important',
        color: theme.palette.secondary.dark,
        '& svg': {
            width: '22px',
            fill: theme.palette.primary.main,
        },
        [`& .active, & .${listItemClasses.root}:hover`]: {
            color: theme.palette.text.secondary,
            '& svg': {
                width: '22px',
                fill: theme.palette.text.secondary,
            },
        },
    },
    listActive: {
        padding: '0px !important',
        color: theme.palette.text.secondary,
        '& svg': {
            width: '22px',
            fill: theme.palette.text.secondary,
        },
    },
    listProfile: {
        padding: '0px !important',
        color: theme.palette.secondary.dark,
        '& svg': {
            width: '20px',
        },
    },
    listItem: {
        padding: '10px 20px !important',
        margin: '0px !important',
        '&:hover': { background: `${theme.palette.primary.main} !important` },
        '&$selected': {
            background: `${theme.palette.primary.main} !important`,
            '&:hover': {
                background: `${theme.palette.primary.main} !important`,
            },
        },
    },
    listItemActive: {
        padding: '10px 20px !important',
        margin: '0px !important',
        background: `${theme.palette.primary.main} !important`,
        boxShadow: theme.palette.getContrastText('shadow-bottom-blue'),
    },
    listItemProfile: {
        padding: '10px 20px !important',
        margin: '0px !important',
        background: '#F5F5F5 !important',
    },
    selected: {},
    listItemIconLeft: {
        minWidth: '30px !important',
    },
    divider: {
        margin: '0px 15px !important',
        color: `${theme.palette.text.disabled} !important`,
    },
}))

const StyledDivMenuContainer = styled('div')(({ theme }) => ({
    backgroundColor: theme.palette.text.secondary,
    boxShadow: theme.palette.getContrastText('shadow-bottom-gray'),
}))

const StyledTypoName = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
}))

const StyledTypoEmail = styled(Typography)(({ theme }) => ({
    fontSize: '14px',
    color: theme.palette.text.disabled,
}))

const StyledLink = styled(Link)(({ theme }) => ({
    color: 'inherit',
    textDecoration: 'none',
}))
